import React from 'react'
import { ErrorBoundary } from './components'
import { HooksDetailedIcon, PauseIcon, ArrowDownIcon } from '@bizfirst/common-react'

const hooks = [
  {
    name: 'LogHook',
    category: 'Diagnostic',
    description: 'Logging and diagnostic purposes for debugging message flow',
    priority: 'Medium',
    canPause: false,
  },
  {
    name: 'FilterHook',
    category: 'Processing',
    description: 'Filter messages by predicate - aborts if predicate returns false',
    priority: 'High',
    canPause: false,
  },
  {
    name: 'ValidationHook',
    category: 'Validation',
    description: 'Validate message against JSON schema - aborts if validation fails',
    priority: 'High',
    canPause: false,
  },
  {
    name: 'DecryptHook',
    category: 'Security',
    description: 'Decrypt encrypted message body using configured cipher',
    priority: 'High',
    canPause: false,
  },
  {
    name: 'VerifySignatureHook',
    category: 'Security',
    description: 'Verify message signature - aborts if signature is invalid',
    priority: 'High',
    canPause: false,
  },
  {
    name: 'AcknowledgeHook',
    category: 'Communication',
    description: 'Send acknowledgment back to server that message was received',
    priority: 'Medium',
    canPause: false,
  },
  {
    name: 'RenderToFormHook',
    category: 'Interactive',
    description: 'Render form or dialog to user - can pause pipeline for user interaction',
    priority: 'Low',
    canPause: true,
  },
  {
    name: 'PublishToSubscribersHook',
    category: 'Terminal',
    description: 'Terminal hook - routes message to all matched topic subscribers',
    priority: 'Medium',
    canPause: false,
  },
  {
    name: 'SignMessageHook',
    category: 'Security',
    description: 'Sign outgoing messages with configured signature algorithm',
    priority: 'Medium',
    canPause: false,
  },
  {
    name: 'EncryptMessageHook',
    category: 'Security',
    description: 'Encrypt outgoing message body using configured cipher',
    priority: 'Medium',
    canPause: false,
  },
  {
    name: 'RetryHook',
    category: 'Resilience',
    description: 'Retry failed message sends with exponential backoff strategy',
    priority: 'Low',
    canPause: false,
  },
  {
    name: 'UiQueryHook',
    category: 'Interactive',
    description: 'Query UI component for user input and response handling',
    priority: 'Low',
    canPause: true,
  },
  {
    name: 'WebMcpHook',
    category: 'Integration',
    description: 'Integrate with WebMCP tools for extended functionality',
    priority: 'Medium',
    canPause: false,
  },
  {
    name: 'FormResponseHook',
    category: 'Interactive',
    description: 'Handle form submission responses and user input validation',
    priority: 'Low',
    canPause: false,
  },
]

const categoryColors: Record<string, string> = {
  Diagnostic: 'var(--color-info)',
  Processing: '#9b59b6',
  Validation: 'var(--obs-danger)',
  Security: 'var(--obs-warning)',
  Communication: '#27ae60',
  Interactive: '#1abc9c',
  Terminal: '#e67e22',
  Resilience: '#34495e',
  Integration: '#16a085',
}

export function HooksReferencePage(): React.ReactElement {
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(null)

  const filteredHooks = selectedCategory
    ? hooks.filter((h) => h.category === selectedCategory)
    : hooks

  const categories = Array.from(new Set(hooks.map((h) => h.category))).sort()

  return (
    <ErrorBoundary>
      <div className="page-container hooks-subscribers-page">
        <div className="page-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><HooksDetailedIcon /> Available Hooks</h2>
        <p className="page-description">
          Complete reference guide to all {hooks.length} available pipeline hooks. Hooks are composable pipeline stages
          that transform messages as they flow through the system.
        </p>
      </div>

      {/* Hooks Section */}
      <section className="section hooks-section">
        <h3>Pipeline Hooks Library</h3>
        <p className="section-description">
          Each hook has a specific responsibility and execution priority. Hooks execute in order based on priority,
          and can abort or pause the pipeline as needed.
        </p>

        {/* Category Filter */}
        <div className="category-filter">
          <button
            className={`filter-btn ${selectedCategory === null ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            All ({hooks.length})
          </button>
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category} ({hooks.filter((h) => h.category === category).length})
            </button>
          ))}
        </div>

        {/* Hooks Grid */}
        <div className="hooks-grid">
          {filteredHooks.map((hook) => (
            <div key={hook.name} className="hook-card">
              <div
                className="hook-category-badge"
                style={{ backgroundColor: categoryColors[hook.category] }}
              >
                {hook.category}
              </div>
              <h4 className="hook-name">{hook.name}</h4>
              <p className="hook-description">{hook.description}</p>
              <div className="hook-meta">
                <span className="meta-item">
                  <strong>Priority:</strong> {hook.priority}
                </span>
                {hook.canPause && <span className="meta-item pause-badge"><PauseIcon /> Can Pause Pipeline</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Hook Execution Flow */}
      <section className="section flow-section">
        <h3>Hook Execution Flow</h3>
        <p className="section-description">Hooks execute in priority order, allowing early termination:</p>

        <div className="flow-diagram">
          <div className="flow-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Message Received</h4>
              <p>Incoming envelope from server</p>
            </div>
          </div>

          <div className="flow-arrow"><ArrowDownIcon /></div>

          <div className="flow-step highlight">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Filter & Validate</h4>
              <p>FilterHook, ValidationHook, VerifySignatureHook</p>
            </div>
          </div>

          <div className="flow-arrow"><ArrowDownIcon /></div>

          <div className="flow-step highlight">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Transform & Decrypt</h4>
              <p>DecryptHook, LogHook, custom processing</p>
            </div>
          </div>

          <div className="flow-arrow"><ArrowDownIcon /></div>

          <div className="flow-step highlight">
            <div className="step-number">4</div>
            <div className="step-content">
              <h4>Interactive (Optional)</h4>
              <p>RenderToFormHook, UiQueryHook - can pause pipeline</p>
            </div>
          </div>

          <div className="flow-arrow"><ArrowDownIcon /></div>

          <div className="flow-step terminal">
            <div className="step-number">5</div>
            <div className="step-content">
              <h4>Terminal Hook</h4>
              <p>PublishToSubscribersHook - routes to subscribers</p>
            </div>
          </div>
        </div>

        <div className="flow-notes">
          <p>
            <strong>Note:</strong> If any hook returns <code>continue: false</code> or calls{' '}
            <code>abort()</code>, pipeline execution terminates at that point. Interactive hooks can pause
            execution, awaiting user input before resuming.
          </p>
        </div>
      </section>

      {/* Hook Categories Reference */}
      <section className="section">
        <h3>Hook Categories</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {categories.map((category) => (
            <div key={category} style={{ padding: '16px', background: 'rgba(15, 23, 42, 0.95)', borderRadius: '12px', border: '1px solid rgba(58, 140, 69, 0.4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: categoryColors[category] }} />
                <h4 style={{ margin: 0, color: '#f8fafc' }}>{category}</h4>
              </div>
              <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                {hooks.filter((h) => h.category === category).length} hook{hooks.filter((h) => h.category === category).length !== 1 ? 's' : ''}
              </p>
            </div>
          ))}
          </div>
        </section>
      </div>
    </ErrorBoundary>
  )
}
